import { useMemo } from 'react';
import { getTagSearch } from 'js/social/api/tag_search_api';
import { FollowableUser, Tag } from '../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '../useDebounce';

/**
 * Given an @-tag query, returns the associated users
 * along with whether it's still loading.
 * @param search The @-tag search query.
 * @param tags The current tags.
 */
export function useTagSearch(
  search: string | undefined,
  tags: Tag[],
): { isLoading: boolean; users: FollowableUser[] } {
  const client = useQueryClient();
  const userTagIds = useMemo(() => {
    return new Set(
      tags.filter((tag) => tag.tagType === 'user').map((tag) => tag.tagId),
    );
  }, [tags]);

  const queryKey = ['social-tag-search', search];
  const invalidated = client.getQueryState(queryKey)?.isInvalidated ?? true;
  const { value: debouncedSearch, debouncing } = useDebounce(
    search,
    invalidated ? 250 : 0,
  );
  const { data: users, isSuccess: querySuccess } = useQuery({
    enabled: debouncedSearch !== undefined,
    staleTime: 60000,
    queryKey: ['social-tag-search', debouncedSearch],
    queryFn: () => getTagSearch(debouncedSearch ?? ''),
  });

  return {
    isLoading: debouncing || !querySuccess,
    users: (users ?? []).filter((user) => !userTagIds.has(user.id)),
  };
}
